/**
 * 
 * @copyright     (c) 2024-2025 Gist Applications Inc.
 * @author        Zac Fair zac@gist-apps.com
 * @package       gift-card-pro
 * 
 * /assets/js/React/Proxy/pages/GiftCardForm/_sections/RecipientDetailsPhysical.js
 * Created:       Thu Aug 29 2024
 * Modified By:   Zac Fair
 * Last Modified: Mon Mar 24 2025
 * -----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	---------------------------------------------------------
 */

import React, {useEffect, useContext} from 'react';
import {FormSection, TextField, CountrySelect, ProvinceSelect, PhoneCodeSelect} from "../../../_components";
import {TranslationsContext, FormContext} from '@proxy/_contexts';
import {useAddressFormatter, useCountryProvince} from "@root/_hooks";

export default function RecipientDetailsPhysical() {

  const translations = useContext(TranslationsContext);

  const {
    formData,
    formErrors,
    updateFormField,
    bulkOrder,
  }  = useContext(FormContext);

  const { stackedFieldOrder, formattedAddress } = useAddressFormatter(
    formData.properties._gcp_address,
    bulkOrder,
    (window.Shopify) ? window.Shopify.locale : "en"
  );

  /**
   * Update address state on input change.
   */
  const handleAddressChange = (field, value) => updateFormField(`properties._gcp_address.${field}`, value);

  const handleProvinceChange = (v) => handleAddressChange("province", v);

  const { countryRef, countryEl, provinces } = useCountryProvince((!bulkOrder) ? handleProvinceChange : null);

  useEffect(() => {
    updateFormField("properties._gcp_recipient_name", formData.properties._gcp_address.firstName);
    updateFormField("properties._gcp_formatted_address", formattedAddress);
  }, [formattedAddress]);

  useEffect(() => {

  }, [formData?.properties?._gcp_address?.firstName]);

  useEffect(() => {
    if (countryEl && countryEl.selectedIndex === 0 && !formData.properties._gcp_address.country) {
      handleAddressChange("country", countryEl.options[0].value);
    }
  }, [countryEl, countryEl?.selectedIndex]);

  const firstNameMarkup = (
    <TextField
      key="firstName"
      value={formData.properties._gcp_address.firstName || ""}
      label={translations.first_name_label}
      type={"text"}
      name="properties[_gcp_address][firstName]"
      onChange={(e) => handleAddressChange("firstName", e.target.value)}
      error={formErrors["firstName"]}
    />
  );

  const lastNameMarkup = (
    <TextField
      key="lastName"
      value={formData.properties._gcp_address.lastName || ""}
      label={translations.last_name_label}
      type={"text"}
      name="properties[_gcp_address][lastName]"
      onChange={(e) => handleAddressChange("lastName", e.target.value)}
      error={formErrors["lastName"]}
    />
  );

  const address1Markup = (
    <TextField
      key="address1"
      value={formData.properties._gcp_address.address1 || ""}
      label={translations.address1_label}
      type={"text"}
      name="properties[_gcp_address][address1]"
      onChange={(e) => handleAddressChange("address1", e.target.value)}
      error={formErrors["address1"]}
    />
  );

  const address2Markup = (
    <TextField
      key="address2"
      value={formData.properties._gcp_address.address2 || ""}
      label={translations.address2_label}
      type={"text"}
      name="properties[_gcp_address][address2]"
      onChange={(e) => handleAddressChange("address2", e.target.value)}
      error={formErrors["address2"]}
    />
  );

  const cityMarkup = (
    <TextField
      key="city"
      value={formData.properties._gcp_address.city || ""}
      label={translations.city_label}
      type={"text"}
      name="properties[_gcp_address][city]"
      onChange={(e) => handleAddressChange("city", e.target.value)}
      error={formErrors["city"]}
    />
  );

  const countryMarkup = (
    <CountrySelect
      key="country"
      value={formData.properties._gcp_address.country || ""}
      label={translations.country_label}
      name="properties[_gcp_address][country]"
      optionsString={(window.GIST && window.GIST.country_option_tags) ? window.GIST.country_option_tags : ""}
      selectRef={countryRef}
      onChange={(e) => handleAddressChange("country", e.target.value)}
      error={formErrors["country"]}
    />
  );

  const provinceMarkup = (
    <ProvinceSelect
      key="province"
      value={formData.properties._gcp_address.province || ""}
      label={translations.province_label}
      name="properties[_gcp_address][province]"
      provinces={provinces}
      onChange={(e) => handleProvinceChange(e.target.value)}
      error={formErrors["province"]}
    />
  );

  const zipMarkup = (
    <TextField
      key="zip"
      value={formData.properties._gcp_address.zip || ""}
      label={translations.zip_label}
      type={"text"}
      name="properties[_gcp_address][zip]"
      onChange={(e) => handleAddressChange("zip", e.target.value)}
      error={formErrors["zip"]}
    />
  );

  const phoneMarkup = (
    <PhoneCodeSelect
      key="phone"
      value={formData.properties._gcp_address.phone || ""}
      label={translations.phone_label}
      placeholder={translations.recipient_mobile_number_placeholder}
      type={"tel"}
      name="properties[_gcp_address][phone]"
      validation="phone"
      onChange={(newValue) => handleAddressChange("phone", newValue)}
      error={formErrors["phone"]}
    />
  );

  const errorMarkup = (formErrors["_gcp_formatted_address"]) ?
    <p className={"gc__input_field_error_message"}>
      { formErrors["_gcp_formatted_address"].message }
    </p>
  : null;

  /**
   * Render fields basd on the addressFormtter fieldOrder.
   */
  const renderField = (field) => {

    switch (field) {
      case "firstName":
        return firstNameMarkup;

      case "lastName":
        return lastNameMarkup;

      case "address1":
        return address1Markup;

      case "address2":
        return address2Markup;

      case "city":
        return cityMarkup;

      case "province":
        return provinceMarkup;

      case "country":
        return countryMarkup;

      case "zip":
        return zipMarkup;

      case "phone":
        return phoneMarkup;

      default:
        return null;
    }

  };

  const hasCityField = stackedFieldOrder?.includes("city");

  return (
    <FormSection title={ translations.recipient_details_heading }>
      <div className={"gc__message_details_div"}>
        {(stackedFieldOrder) ?
          stackedFieldOrder.map((field) => renderField(field))
        : null}
        {!hasCityField ? renderField("city") : null}
      </div>
      {errorMarkup}
    </FormSection>
  );

};
